import React, { useEffect, useState } from 'react';
import AdminTable from 'common/AdminTable';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import axiosInstance from 'apis/axiosConfig';

const KycUsersList = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('customer');
            if (res?.data && Array.isArray(res.data)) {
                // Ensure newest registered users appear at the absolute top row dynamically
                const sortedList = [...res.data].sort((a, b) => new Date(b.createdAt || Date.now()) - new Date(a.createdAt || Date.now()));
                setData(sortedList);
            } else {
                setData([]);
            }
        } catch (err) {
            console.error("Error fetching live users data:", err);
            toast.error("Failed to fetch live synchronized users list.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleActionClick = (row) => {
        navigate(`/users/update/${row.id}`);
    };

    const tableHeaders = [
        { Header: 'ID', accessor: 'id', Cell: ({ row }) => <strong style={{ color: '#0284C7' }}>#{row.original.id}</strong> },
        { 
            Header: 'Borrower Name', 
            accessor: 'name', 
            Cell: ({ row }) => (
                <div className="text-left font-weight-bold" style={{ color: '#0F172A', fontSize: '15px' }}>
                    {row.original.name ? `${row.original.name} ${row.original.last_name || ''}`.trim() : row.original.account_holder_name || 'Borrower Profile'}
                    {/* Add a subtle highlight label for the most recent entry */}
                    {data.indexOf(row.original) === 0 && (
                        <span className="badge badge-success ml-2 py-1 px-2 animate__animated animate__pulse animate__infinite" style={{ fontSize: '10px', borderRadius: '4px' }}>
                            ✨ NEWLY ADDED
                        </span>
                    )}
                </div>
            ) 
        },
        { 
            Header: 'User Type', 
            accessor: 'user_type', 
            Cell: ({ row }) => {
                const isInvestor = row.original.user_type === 'investor';
                return (
                    <span 
                        className="badge py-1 px-2 font-weight-bold animate__animated animate__fadeIn" 
                        style={{ 
                            borderRadius: '6px', 
                            backgroundColor: isInvestor ? '#F3E8FF' : '#E2F1F1', 
                            color: isInvestor ? '#7E22CE' : '#082032', 
                            border: `1px solid ${isInvestor ? '#E9D5FF' : '#BAE6FD'}` 
                        }}
                    >
                        {isInvestor ? 'Investor' : 'User'}
                    </span>
                );
            }
        },
        { Header: 'Mobile Contact', accessor: 'mobile', Cell: ({ row }) => <span style={{ fontWeight: '600', color: '#334155' }}>{row.original.mobile || 'N/A'}</span> },
        { Header: 'PAN Identity', accessor: 'pan_number', Cell: ({ row }) => <span className="text-uppercase font-weight-bold" style={{ color: '#475569', letterSpacing: '0.5px' }}>{row.original.pan_number || 'N/A'}</span> },
        { Header: 'Bank Account/No', accessor: 'bank_account_number', Cell: ({ row }) => <span style={{ fontFamily: 'monospace', fontSize: '14px', fontWeight: '700' }}>{row.original.bank_account_number || 'N/A'}</span> },
        { 
            Header: 'Audit Status', 
            accessor: 'is_active', 
            Cell: ({ row }) => {
                const isActive = row.original.is_active === 1 || row.original.is_active === true;
                return (
                    <span 
                        className="badge py-2 px-3 font-weight-bold" 
                        style={{ 
                            borderRadius: '20px', 
                            fontSize: '12px',
                            backgroundColor: isActive ? '#D1FAE5' : '#FEF3C7',
                            color: isActive ? '#065F46' : '#B45309',
                            border: `1px solid ${isActive ? '#A7F3D0' : '#FDE68A'}`
                        }}
                    >
                        {isActive ? '✓ Active & Verified' : '⏳ Pending Approval'}
                    </span>
                );
            } 
        },
        { 
            Header: 'Onboarded Date', 
            accessor: 'createdAt', 
            Cell: ({ row }) => (
                <span style={{ fontSize: '13px', color: '#64748B', fontWeight: '600' }}>
                    {typeof row.original.createdAt === 'string' ? row.original.createdAt : 'Recent'}
                </span>
            ) 
        },
        { 
            Header: 'Actions', 
            accessor: 'action', 
            Cell: ({ row }) => (
                <div className="d-flex alignItems-center">
                    <button 
                        type="button"
                        className="btn btn-sm font-weight-bold shadow-sm" 
                        style={{ backgroundColor: '#E2F1F1', color: '#082032', borderRadius: '8px', padding: '6px 12px', border: 0 }}
                        onClick={() => handleActionClick(row.original)}
                    >
                        🔍 View
                    </button>
                </div>
            ) 
        }
    ];
    return (
        <div className="container-fluid mt-4 mb-5" style={{ fontFamily: "'Inter', 'Roboto', sans-serif" }}>
            {/* Clean Header */}
            <div className="d-flex justify-content-between alignItems-center mb-4 px-2">
                <h2 className="font-weight-bold mb-0" style={{ color: '#0F172A' }}>User List</h2>
                <button 
                    className="btn btn-outline-primary font-weight-bold px-3 py-1 shadow-sm"
                    onClick={fetchData}
                    disabled={loading}
                    style={{ borderRadius: '8px', fontSize: '13px' }}
                >
                    {loading ? 'Refreshing...' : '🔄 Force Sync'}
                </button>
            </div>

            {/* Table Container */}
            <div className="card border-0 shadow-sm bg-white p-4" style={{ borderRadius: '20px' }}>
                {loading && data.length === 0 ? (
                    <div className="text-center py-5 my-5">
                        <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                        <h5 className="font-weight-bold mt-3 text-secondary">Fetching Synchronized Borrower Ledgers...</h5>
                    </div>
                ) : data.length > 0 ? (
                    <div className="animate__animated animate__fadeIn">
                        <AdminTable tableHeaders={tableHeaders} tableData={data} />
                    </div>
                ) : (
                    <div className="text-center py-5 my-4 bg-light rounded-lg border">
                        <span style={{ fontSize: '40px' }}>📭</span>
                        <h5 className="font-weight-bold mt-2 text-dark">No Mapped Borrowers or KYC Records Available</h5>
                        <p className="text-muted mb-0">Use the &apos;Add User&apos; option under Loan User menu to onboard new entities dynamically.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default KycUsersList;

